import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { storefrontService } from '../services/storefrontService.js';

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [cart, setCart] = useState({ items: [], quantity: 0, subtotal: 0 });
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);

  const loadCart = useCallback(async () => {
    setStatus('loading');
    try {
      const payload = await storefrontService.cart();
      setCart(payload.cart);
      setError(null);
      setStatus('ready');
    } catch (err) {
      setError(err.message);
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    loadCart();
  }, [loadCart]);

  const runAction = useCallback(async (body) => {
    setStatus('saving');
    try {
      const payload = await storefrontService.cartAction(body);
      setCart(payload.cart);
      setError(null);
      setStatus('ready');
      return payload.cart;
    } catch (err) {
      setError(err.message);
      setStatus('error');
      throw err;
    }
  }, []);

  const value = useMemo(
    () => ({
      cart,
      status,
      error,
      refresh: loadCart,
      addItem: (item) => runAction({ action: 'add', ...item }),
      updateItem: (item) => runAction({ action: 'update', ...item }),
      removeItem: (item) => runAction({ action: 'remove', ...item }),
      clearCart: () => runAction({ action: 'clear' }),
    }),
    [cart, status, error, loadCart, runAction],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used inside CartProvider');
  }

  return context;
}
